import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save, Building2, Upload, Trash2, Users as UsersIcon, X as XIcon, UserPlus } from "lucide-react";
import { criarProjeto, atualizarProjeto, buscarProjeto, uploadFotoProjeto, removerFotoProjeto, listarMembrosProjeto, definirMembrosProjeto } from "../../services/projetos";
import { ProjetoAvatar } from "../../components/ProjetoAvatar";
import { listarUsuariosAtivos } from "../../services/usuarios";
import { Avatar } from "../../components/Avatar";
import { listarGerentes, GerenteOption } from "../../services/usuarios";
import { listarClientes, criarCliente } from "../../services/clientes";
import { listarTiposProjeto } from "../../services/tiposProjeto";
import { listarTiposTarefa } from "../../services/tiposTarefa";
import { useAuth } from "../../hooks/useAuth";
import { SearchableSelect } from "../../components/SearchableSelect";
import { MoneyInput } from "../../components/MoneyInput";
import { useRascunho } from "../../hooks/useRascunho";
import { ClienteFormModal } from "../clientes/ClienteFormModal";
import { listarProjetoTags } from "../../services/projetoTags";
import { TagChip } from "../../components/TagChip";

// Prazo padrão: base (ou hoje) + 3 meses, em yyyy-mm-dd. Evita projeto sem prazo
// (que aparecia como "31/12/1969 atrasado"). Pedido Thainá: default 3 meses à frente.
function prazoPadrao(baseISO?: string): string {
  const d = baseISO ? new Date(baseISO) : new Date();
  if (isNaN(d.getTime())) return prazoPadrao();
  d.setMonth(d.getMonth() + 3);
  return d.toISOString().split("T")[0];
}

// Item selecionado no projeto: quantidade + formato + especificações (detalhe) +
// horas vendidas POR NÍVEL (strings na UI; convertidas no payload). CADASTRO 1.
type ItemSel = {
  quantidade: number;
  formato: string;
  especificacoes: string;
  horasJunior: string;
  horasPleno: string;
  horasSenior: string;
};
const ITEM_SEL_VAZIO: ItemSel = { quantidade: 1, formato: "", especificacoes: "", horasJunior: "", horasPleno: "", horasSenior: "" };

const FORMATOS_PADRAO = ["vertical", "horizontal", "feed", "story"];
const capFmt = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Multi-seleção de formato (Cadastro #1): vários formatos por item + adicionar
 * customizados. Guarda como string separada por vírgula (campo `formato`). */
function FormatoMultiSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [novo, setNovo] = useState("");
  const sel = value ? value.split(",").map((s) => s.trim()).filter(Boolean) : [];
  const chips = Array.from(new Set([...FORMATOS_PADRAO, ...sel]));
  const toggle = (f: string) => onChange((sel.includes(f) ? sel.filter((x) => x !== f) : [...sel, f]).join(","));
  const addCustom = () => {
    const f = novo.trim().toLowerCase();
    if (f && !sel.map((s) => s.toLowerCase()).includes(f)) onChange([...sel, f].join(","));
    setNovo("");
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {chips.map((f) => (
        <button
          key={f}
          type="button"
          onClick={() => toggle(f)}
          className={`text-xs px-2 py-1 rounded-full border transition-colors ${
            sel.includes(f) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
          }`}
        >
          {capFmt(f)}
        </button>
      ))}
      <input
        type="text"
        value={novo}
        onChange={(e) => setNovo(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
        onBlur={addCustom}
        placeholder="+ formato"
        className="w-24 px-2 py-1 rounded-md border border-slate-200 text-xs outline-none focus:border-blue-400"
      />
    </div>
  );
}

export function ProjetoNovo() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, can } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const ehEdicao = !!id;
  const internoInicial = searchParams.get("interno") === "1";

  const [gerentes, setGerentes] = useState<GerenteOption[]>([]);
  const { data: tipos = [] } = useQuery({
    queryKey: ["tipos-projeto", "ativos"],
    queryFn: () => listarTiposProjeto(),
  });
  const [loadingGerentes, setLoadingGerentes] = useState(true);

  const { data: clientes = [] } = useQuery({ queryKey: ["clientes"], queryFn: listarClientes });
  // Tags de projeto (só ativas) — o picker do form.
  const { data: projetoTagsCatalogo = [] } = useQuery({ queryKey: ["projeto-tags"], queryFn: () => listarProjetoTags() });

  // MELHORIA 8 — catálogo unificado: os itens do contrato são as entradas
  // marcadas como "vendável" no catálogo de Tipos. Inclui inativos (todos) pra
  // mostrar itens já vinculados mesmo que tenham sido desativados depois.
  const { data: tiposTodos = [] } = useQuery({
    queryKey: ["tipos-tarefa", "todos"],
    queryFn: () => listarTiposTarefa({ todos: true }),
  });
  const itensCatalogoTodos = tiposTodos.filter((t) => t.vendavel);
  // Itens selecionados: tipoTarefaId -> { quantidade, especificações, horas vendidas }.
  const [itensSel, setItensSel] = useState<Record<string, ItemSel>>({});
  // Mostra itens ativos + qualquer item já selecionado (mesmo desativado depois).
  const itensCatalogo = itensCatalogoTodos.filter((item) => item.ativo || itensSel[item.id] != null);

  const { data: projetoExistente, isLoading: carregandoProjeto } = useQuery({
    queryKey: ["projeto", id],
    queryFn: () => buscarProjeto(id!),
    enabled: ehEdicao,
  });

  const [form, setForm] = useState({
    nome: "",
    cliente: "",
    clienteId: "",
    interno: internoInicial,
    modalidade: "avulso", // avulso | mensal — só relevante quando não é interno
    periodicidade: "", // melhoria 26 — rótulo: "" | mensal | trimestral | anual
    etiquetas: [] as string[], // tags do projeto (slugs) — substituem a prioridade
    tipo: "dev",
    status: "planejamento",
    dataInicio: new Date().toISOString().split("T")[0],
    prazoEntrega: prazoPadrao(),
    dataEntregaCliente: "", // MELHORIA 16 — data combinada com o cliente (só gestão)
    dataAssinatura: "",
    // CADASTRO 5: mensal usa fim de contrato (+ indeterminado); avulso usa prazo em texto.
    fimContrato: "",
    contratoIndeterminado: false,
    prazoTexto: "",
    tempoDesenvolvimentoDias: "",
    responsavelId: "",
    descricao: "",
    resumoPublico: "",
    observacoes: "",
    prioridade: "media",
    dataPrimeiraEntrega: "",
    prazoFeedback: "",
    inicioSuporte: "",
    fimSuporte: "",
    valor: "",
    valorRecorrente: "",
    // CADASTRO 7: custo extra para reembolso (separado do valor do projeto).
    custoExtra: "",
    custoExtraDescricao: "",
  });

  // Rascunho automático (Fase 02 #9/#10): só na criação (edição já tem fonte).
  const rascunho = useRascunho<{ form: any; itensSel: any }>("projeto_novo", !ehEdicao);
  useEffect(() => {
    if (ehEdicao) return;
    // NÃO persiste um form vazio — senão o save do próprio mount sobrescreveria um
    // rascunho real com os defaults brancos (bug que derrotava a feature no reload).
    const temConteudo =
      form.nome.trim() !== "" || form.cliente.trim() !== "" || (form.descricao || "").trim() !== "" || Object.keys(itensSel).length > 0;
    if (!temConteudo) return;
    // Privacidade: NÃO guarda valores financeiros no localStorage (pricing sensível).
    const formSeguro: any = { ...form };
    delete formSeguro.valor; delete formSeguro.valorRecorrente; delete formSeguro.custoExtra; delete formSeguro.custoExtraDescricao;
    const itensSeguro = Object.fromEntries(Object.entries(itensSel).map(([k, v]) => [k, { ...(v as ItemSel), horasJunior: "", horasPleno: "", horasSenior: "" }]));
    rascunho.salvar({ form: formSeguro, itensSel: itensSeguro });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, itensSel]);
  function restaurarRascunho() {
    const r = rascunho.rascunhoInicial;
    if (r?.form) setForm((f) => ({ ...f, ...r.form })); // merge → mantém defaults p/ campos ausentes
    if (r?.itensSel) setItensSel(r.itensSel);
    rascunho.descartar();
  }

  // Fetch gerentes on mount
  useEffect(() => {
    listarGerentes()
      .then((data) => {
        setGerentes(data);
        if (!ehEdicao) {
          if (user?.perfil === "gestor" || user?.perfil === "admin") {
            setForm((f) => ({ ...f, responsavelId: user.id }));
          } else if (data.length > 0) {
            setForm((f) => ({ ...f, responsavelId: data[0].id }));
          }
        }
      })
      .catch(() => setGerentes([]))
      .finally(() => setLoadingGerentes(false));
  }, [user, ehEdicao]);

  // Hidrata form com dados existentes em modo edição
  useEffect(() => {
    if (!projetoExistente) return;
    const p: any = projetoExistente;
    setForm({
      nome: p.nome || "",
      cliente: p.cliente || "",
      clienteId: p.clienteId || "",
      interno: !!p.interno,
      modalidade: p.modalidade === "mensal" ? "mensal" : "avulso",
      periodicidade: (p as any).periodicidade || "",
      etiquetas: (p as any).etiquetas ? String((p as any).etiquetas).split(/[,\s]+/).filter(Boolean) : [],
      tipo: p.tipo || "dev",
      status: p.status || "planejamento",
      dataInicio: p.dataInicio ? p.dataInicio.split("T")[0] : "",
      // Prazo válido vindo do banco mostra o real; nulo/epoch (1969) cai no default +3 meses.
      prazoEntrega: p.prazoEntrega && new Date(p.prazoEntrega).getFullYear() >= 2000 ? p.prazoEntrega.split("T")[0] : prazoPadrao(p.dataInicio),
      dataEntregaCliente: (p as any).dataEntregaCliente ? String((p as any).dataEntregaCliente).split("T")[0] : "",
      dataAssinatura: p.dataAssinatura ? p.dataAssinatura.split("T")[0] : "",
      fimContrato: p.fimContrato ? p.fimContrato.split("T")[0] : "",
      contratoIndeterminado: !!p.contratoIndeterminado,
      prazoTexto: p.prazoTexto || "",
      tempoDesenvolvimentoDias: p.tempoDesenvolvimentoDias != null ? String(p.tempoDesenvolvimentoDias) : "",
      responsavelId: p.responsavelId || "",
      descricao: p.descricao || "",
      resumoPublico: p.resumoPublico || "",
      observacoes: p.observacoes || "",
      prioridade: p.prioridade || "media",
      dataPrimeiraEntrega: p.dataPrimeiraEntrega ? p.dataPrimeiraEntrega.split("T")[0] : "",
      prazoFeedback: p.prazoFeedback ? p.prazoFeedback.split("T")[0] : "",
      inicioSuporte: p.inicioSuporte ? p.inicioSuporte.split("T")[0] : "",
      fimSuporte: p.fimSuporte ? p.fimSuporte.split("T")[0] : "",
      // 0 é tratado como "sem valor" (== vazio) — evita empate 0/vazio no MoneyInput e no save.
      valor: p.valor ? String(p.valor) : "",
      valorRecorrente: p.valorRecorrente ? String(p.valorRecorrente) : "",
      custoExtra: (p as any).custoExtra ? String((p as any).custoExtra) : "",
      custoExtraDescricao: (p as any).custoExtraDescricao || "",
    });
    // Hidrata itens (quantidade + formato + detalhe + horas por nível).
    if (Array.isArray(p.itensMensais)) {
      const mapa: Record<string, ItemSel> = {};
      for (const it of p.itensMensais) {
        let niv: any = (it as any).horasPorNivel;
        if (typeof niv === "string") { try { niv = JSON.parse(niv); } catch { niv = null; } }
        if (!it.tipoTarefaId) continue;
        mapa[it.tipoTarefaId] = {
          quantidade: it.quantidade || 1,
          formato: (it as any).formato || "",
          especificacoes: (it as any).especificacoes || "",
          horasJunior: niv?.junior ? String(niv.junior) : "",
          horasPleno: niv?.pleno ? String(niv.pleno) : "",
          horasSenior: niv?.senior ? String(niv.senior) : "",
        };
      }
      setItensSel(mapa);
    }
  }, [projetoExistente]);

  const createM = useMutation({
    mutationFn: criarProjeto,
    onSuccess: () => {
      rascunho.limpar(); // projeto criado → descarta o rascunho
      qc.invalidateQueries({ queryKey: ["projetos"] });
      navigate("/projetos");
    },
  });

  const updateM = useMutation({
    mutationFn: (payload: any) => atualizarProjeto(id!, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projetos"] });
      qc.invalidateQueries({ queryKey: ["projeto", id] });
      navigate(`/projetos/${id}`);
    },
  });

  // Modal de novo cliente in-line (atalho pra não precisar sair pra Clientes).
  const [novoClienteAberto, setNovoClienteAberto] = useState(false);
  const criarClienteM = useMutation({
    mutationFn: criarCliente,
    onSuccess: (cli) => {
      qc.invalidateQueries({ queryKey: ["clientes"] });
      // Já seleciona o cliente recém-criado e fecha o modal.
      setForm((f) => ({ ...f, clienteId: cli.id, cliente: cli.nome }));
      setNovoClienteAberto(false);
    },
  });

  const submitting = createM.isPending || updateM.isPending;
  const erro = createM.isError || updateM.isError;
  const erroMsg = (() => {
    const e: any = createM.error || updateM.error;
    const apiErr = e?.response?.data?.error;
    if (!apiErr) return null;
    if (apiErr.issues && typeof apiErr.issues === "object") {
      const linhas: string[] = [];
      for (const [campo, msgs] of Object.entries(apiErr.issues)) {
        if (Array.isArray(msgs) && msgs.length) linhas.push(`${campo}: ${msgs.join(", ")}`);
      }
      if (linhas.length) return linhas.join(" | ");
    }
    return apiErr.message || null;
  })();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Em projeto interno, o backend ignora cliente/valores/feedback/suporte —
    // mas mandamos undefined/null pra deixar o payload limpo.
    const ehInterno = form.interno;
    if (!ehInterno && !form.clienteId) {
      alert("Selecione um cliente cadastrado. Cadastre em Clientes → Novo cliente se não houver.");
      return;
    }
    // Modalidade só vale pra projeto externo. Interno é sempre tratado como avulso.
    const ehMensal = !ehInterno && form.modalidade === "mensal";
    // Itens do contrato valem para avulso E mensal (CADASTRO 6); só interno não tem.
    const itensMensaisPayload = !ehInterno
      ? Object.entries(itensSel)
          .filter(([, v]) => v.quantidade > 0)
          .map(([tipoTarefaId, v]) => {
            const hN = (s: string) => (s !== "" && Number(s) > 0 ? Number(s) : 0);
            const jr = hN(v.horasJunior), pl = hN(v.horasPleno), sr = hN(v.horasSenior);
            return {
              tipoTarefaId,
              quantidade: v.quantidade,
              formato: v.formato || null,
              especificacoes: v.especificacoes.trim() || null,
              horasPorNivel: (jr + pl + sr) > 0 ? { junior: jr, pleno: pl, senior: sr } : null,
            };
          })
      : [];
    // Valor ÚNICO por modalidade (CADASTRO 2): mensal usa só a mensalidade
    // (valorRecorrente); avulso usa só o valor cheio (valor). Interno não tem valor.
    const valorNum = !ehInterno && !ehMensal && form.valor !== "" ? Number(form.valor) : null;
    const valorRecNum = !ehInterno && ehMensal && form.valorRecorrente !== "" ? Number(form.valorRecorrente) : null;
    const payload = {
      nome: form.nome,
      // `cliente` (texto legado) NÃO é mais enviado: quem manda é o `clienteId`,
      // e o backend deriva o texto do cliente vinculado. Reenviar o valor que a
      // tela carregou fazia a troca de cliente preservar o nome antigo.
      clienteId: ehInterno ? undefined : (form.clienteId || undefined),
      interno: ehInterno,
      modalidade: ehInterno ? "avulso" : form.modalidade,
      // MELHORIA 26 — periodicidade (rótulo). Só projeto externo; "" = não definida.
      periodicidade: !ehInterno && form.periodicidade ? form.periodicidade : null,
      // Tags do projeto (substituem a prioridade) — array de slugs.
      etiquetas: form.etiquetas,
      itensMensais: itensMensaisPayload,
      tipo: form.tipo,
      status: form.status,
      dataInicio: form.dataInicio,
      // Mensal: o backend deriva o prazo do fim do contrato. Avulso: default +3 meses.
      prazoEntrega: ehMensal ? null : (form.prazoEntrega || prazoPadrao(form.dataInicio)),
      dataEntregaCliente: !ehInterno && form.dataEntregaCliente ? `${form.dataEntregaCliente}T12:00:00` : null,
      // CADASTRO 5 — datas de contrato por modalidade.
      fimContrato: ehMensal && !form.contratoIndeterminado && form.fimContrato ? form.fimContrato : null,
      contratoIndeterminado: ehMensal ? !!form.contratoIndeterminado : false,
      prazoTexto: !ehInterno && !ehMensal ? (form.prazoTexto.trim() || null) : null,
      dataAssinatura: form.dataAssinatura || null,
      tempoDesenvolvimentoDias: ehMensal || form.tempoDesenvolvimentoDias === "" ? null : Number(form.tempoDesenvolvimentoDias),
      responsavelId: form.responsavelId,
      descricao: form.descricao || undefined,
      resumoPublico: ehInterno ? undefined : (form.resumoPublico || undefined),
      observacoes: form.observacoes || undefined,
      prioridade: form.prioridade,
      dataPrimeiraEntrega: ehInterno ? null : (form.dataPrimeiraEntrega || null),
      prazoFeedback: ehInterno ? null : (form.prazoFeedback || null),
      inicioSuporte: ehInterno ? null : (form.inicioSuporte || null),
      fimSuporte: ehInterno ? null : (form.fimSuporte || null),
      valor: valorNum,
      valorRecorrente: valorRecNum,
      custoExtra: form.custoExtra !== "" ? Number(form.custoExtra) : null,
      custoExtraDescricao: form.custoExtraDescricao.trim() || null,
    } as any;

    // BUG "valor muda sozinho ao salvar": em edição, só reenvia valor/valorRecorrente
    // se mudaram de fato — assim um save que não tocou no valor nunca reescreve o campo.
    if (ehEdicao) {
      const orig: any = projetoExistente || {};
      // Normaliza ambos os lados (Number, com 0 ≡ null) antes de comparar:
      // blinda contra coerção (Float/Decimal) e contra o empate 0/vazio.
      const origValor = orig.valor ? Number(orig.valor) : null;
      const origRec = orig.valorRecorrente ? Number(orig.valorRecorrente) : null;
      if (valorNum === origValor) delete payload.valor;
      if (valorRecNum === origRec) delete payload.valorRecorrente;
      // Mesmo cuidado pro custo extra (não reescrever se não mudou).
      const origCusto = orig.custoExtra ? Number(orig.custoExtra) : null;
      if (payload.custoExtra === origCusto) delete payload.custoExtra;
      if ((payload.custoExtraDescricao || null) === (orig.custoExtraDescricao || null)) delete payload.custoExtraDescricao;
      // Itens: só reenviar se realmente mudaram — evita reescrever o pivot (e
      // regenerar ids/criadoEm que o relatório usa) a cada save bobo. horasPorNivel
      // pode vir como objeto (payload) ou string JSON (API) — normaliza os dois.
      const parseNivel = (v: any) => { if (typeof v === "string") { try { return JSON.parse(v) || {}; } catch { return {}; } } return v || {}; };
      const sigItens = (arr: any[]) => JSON.stringify(
        (arr || [])
          .map((i) => {
            const n = parseNivel(i.horasPorNivel);
            return {
              id: i.tipoTarefaId, q: i.quantidade,
              // formato é um CONJUNTO — normaliza (ordena) pra reordenar chips não virar "mudança".
              f: i.formato ? String(i.formato).split(",").map((s: string) => s.trim().toLowerCase()).filter(Boolean).sort().join(",") : null,
              e: i.especificacoes || null,
              jr: Number(n.junior) || 0, pl: Number(n.pleno) || 0, sr: Number(n.senior) || 0,
            };
          })
          .sort((a, b) => a.id.localeCompare(b.id)),
      );
      if (sigItens(payload.itensMensais) === sigItens(orig.itensMensais)) delete payload.itensMensais;
      updateM.mutate(payload);
    } else {
      createM.mutate(payload);
    }
  };

  if (ehEdicao && carregandoProjeto) {
    return <div className="text-slate-400">Carregando…</div>;
  }

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(ehEdicao ? `/projetos/${id}` : "/projetos")}
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <ArrowLeft size={20} className="text-slate-500" />
        </button>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          {ehEdicao ? "Editar Projeto" : "Novo Projeto"}
          {form.interno && (
            <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 text-xs font-medium uppercase tracking-wide whitespace-nowrap">
              Interno
            </span>
          )}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        {/* Rascunho não salvo encontrado — oferece restaurar (Fase 02 #9/#10). */}
        {!ehEdicao && rascunho.temRascunho && (
          <div className="flex items-center justify-between gap-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm">
            <span className="text-blue-800">Você tem um rascunho não salvo deste formulário.</span>
            <div className="flex items-center gap-2 shrink-0">
              <button type="button" onClick={restaurarRascunho} className="px-2.5 py-1 rounded bg-blue-600 text-white text-xs font-medium hover:bg-blue-700">Restaurar</button>
              <button type="button" onClick={() => rascunho.descartar()} className="px-2.5 py-1 rounded bg-white border border-slate-200 text-slate-600 text-xs hover:bg-slate-50">Descartar</button>
            </div>
          </div>
        )}
        {/* Marcar como interno só na CRIAÇÃO. Na edição, fica read-only. */}
        {!ehEdicao && (
          <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors">
            <input
              type="checkbox"
              checked={form.interno}
              onChange={(e) => setForm({ ...form, interno: e.target.checked })}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 text-slate-600 focus:ring-slate-500"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                <Building2 size={16} className="text-slate-500" />
                Interno
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Sem cliente e sem cobrança — para iniciativas internas (refator, infra, marketing próprio, R&D).
              </p>
            </div>
          </label>
        )}

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Nome do Projeto *</label>
          <input type="text" required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none" placeholder="Ex: E-commerce Cliente X" />
        </div>

        {/* Imagem/avatar do projeto. Disponível só em edição (precisa do id pra subir). */}
        {ehEdicao && id && (
          <FotoProjetoEditor
            projetoId={id}
            nome={form.nome}
            fotoUrl={(projetoExistente as any)?.fotoUrl || null}
            cor={tipos.find((t) => t.slug === form.tipo)?.cor || "#64748b"}
            onChange={() => qc.invalidateQueries({ queryKey: ["projeto", id] })}
          />
        )}

        {/* Pessoas (atendimento + executores) — só em edição, porque executor é
            gravado por mutation própria e depende do projeto já existir. Na
            CRIAÇÃO aparece só o select de atendimento mais abaixo. */}
        {ehEdicao && id && (
          <PessoasProjeto
            projetoId={id}
            responsavelId={form.responsavelId}
            gerentes={gerentes}
            onChangeResponsavel={(rid) => setForm((f) => ({ ...f, responsavelId: rid }))}
          />
        )}

        {!form.interno && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Cliente <span className="text-red-500">*</span>
            </label>
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <SearchableSelect
                  items={clientes.filter((c) => c.ativo)}
                  value={form.clienteId}
                  onChange={(idCli) => {
                    const c = clientes.find((x) => x.id === idCli);
                    setForm({ ...form, clienteId: idCli, cliente: c?.nome || "" });
                  }}
                  getId={(c) => c.id}
                  getLabel={(c) => c.nome}
                  getSecondary={(c) => c.codigo || c.email || c.documento || undefined}
                  placeholder="Selecione um cliente cadastrado…"
                />
              </div>
              <button
                type="button"
                onClick={() => setNovoClienteAberto(true)}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-sm bg-blue-600 hover:bg-blue-700 text-white"
                title="Cadastrar cliente sem sair desta tela"
              >
                <UserPlus size={14} /> Novo
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Apenas clientes cadastrados podem receber projeto. Clique em <strong>Novo</strong> para cadastrar aqui mesmo.
            </p>
          </div>
        )}

        {/* Modalidade de contratação — só para projeto externo (cliente). */}
        {!form.interno && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Modalidade *</label>
            <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
              {[
                { id: "avulso", label: "Avulso" },
                { id: "mensal", label: "Mensal" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setForm({ ...form, modalidade: opt.id })}
                  className={
                    "px-5 py-2 text-sm font-medium transition-colors " +
                    (form.modalidade === opt.id
                      ? "bg-blue-600 text-white"
                      : "bg-white text-slate-600 hover:bg-slate-50")
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              {form.modalidade === "mensal"
                ? "Contrato recorrente: selecione os itens e quantidades abaixo. Prioridade e tempo de desenvolvimento não se aplicam."
                : "Projeto pontual, com prioridade e prazo de desenvolvimento."}
            </p>
          </div>
        )}

        {/* MELHORIA 26 — periodicidade (só rótulo/etiqueta; não gera nada). Ex.:
            projeto anual com entregas mensais. Só projeto externo. */}
        {!form.interno && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Periodicidade</label>
            <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
              {[
                { id: "", label: "Não definir" },
                { id: "mensal", label: "Mensal" },
                { id: "trimestral", label: "Trimestral" },
                { id: "anual", label: "Anual" },
              ].map((opt) => (
                <button
                  key={opt.id || "nenhuma"}
                  type="button"
                  onClick={() => setForm({ ...form, periodicidade: opt.id })}
                  className={
                    "px-4 py-2 text-sm font-medium transition-colors border-l first:border-l-0 border-slate-300 " +
                    (form.periodicidade === opt.id
                      ? "bg-blue-600 text-white"
                      : "bg-white text-slate-600 hover:bg-slate-50")
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Só um rótulo para organizar (ex.: projeto <strong>anual</strong> com entregas mensais). Não muda o funcionamento do projeto.
            </p>
          </div>
        )}

        {/* TAGS DO PROJETO — substituem o nível de urgência/prioridade (pedido da
            Rec: "os projetos têm todos a mesma importância"). Catálogo próprio
            (ProjetoTag), gerenciado em Admin → Tags de projeto. Clica pra
            ligar/desligar cada tag. */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Tags <span className="text-slate-400 font-normal">(opcional — clique para marcar)</span>
          </label>
          {projetoTagsCatalogo.length === 0 ? (
            <p className="text-xs text-slate-400">
              Nenhuma tag de projeto cadastrada. Gestor/admin cria em <strong>Admin → Tags de projeto</strong>.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {projetoTagsCatalogo.map((tg) => {
                const marcada = form.etiquetas.includes(tg.slug);
                return (
                  <button
                    key={tg.id}
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        etiquetas: marcada
                          ? form.etiquetas.filter((s) => s !== tg.slug)
                          : [...form.etiquetas, tg.slug],
                      })
                    }
                    className={`rounded-full transition-all ${marcada ? "ring-2 ring-blue-500 ring-offset-1" : "opacity-60 hover:opacity-100"}`}
                    title={marcada ? `${tg.nome} — clique para tirar` : `${tg.nome} — clique para marcar`}
                  >
                    <TagChip tag={tg} />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Itens do contrato (avulso e mensal) — multi-seleção com quantidade. */}
        {!form.interno && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Itens do contrato</label>
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
              {itensCatalogo.length === 0 && (
                <p className="text-sm text-slate-400 px-3 py-3">
                  Nenhum item vendável no catálogo. Cadastre em Configurações → Catálogo (marque "Vende em contrato").
                </p>
              )}
              {itensCatalogo.map((item) => {
                const sel = itensSel[item.id];
                const marcado = sel != null;
                return (
                  <div key={item.id} className="px-3 py-2">
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 flex-1 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={marcado}
                          onChange={(e) => {
                            setItensSel((prev) => {
                              const next = { ...prev };
                              if (e.target.checked) next[item.id] = prev[item.id] || ITEM_SEL_VAZIO;
                              else delete next[item.id];
                              return next;
                            });
                          }}
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-slate-700">{item.label}</span>
                      </label>
                      {marcado && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-[11px] text-slate-400">Qtd</span>
                          <input
                            type="number"
                            min={1}
                            step={1}
                            value={sel.quantidade}
                            onChange={(e) => {
                              const q = Math.max(1, Math.trunc(Number(e.target.value) || 1));
                              setItensSel((prev) => ({ ...prev, [item.id]: { ...prev[item.id], quantidade: q } }));
                            }}
                            className="w-16 px-2 py-1 rounded-md border border-slate-300 text-sm text-center outline-none focus:border-blue-500"
                          />
                        </div>
                      )}
                    </div>
                    {marcado && (
                      <div className="mt-2 pl-6 space-y-2">
                        <div className="space-y-1.5">
                          <FormatoMultiSelect
                            value={sel.formato}
                            onChange={(v) => setItensSel((prev) => ({ ...prev, [item.id]: { ...prev[item.id], formato: v } }))}
                          />
                          <input
                            type="text"
                            value={sel.especificacoes}
                            onChange={(e) => setItensSel((prev) => ({ ...prev, [item.id]: { ...prev[item.id], especificacoes: e.target.value } }))}
                            placeholder="Detalhe (ex.: 60s, 1080x1080, outdoor 9x3m)"
                            className="w-full px-2 py-1 rounded-md border border-slate-200 text-sm outline-none focus:border-blue-400"
                          />
                        </div>
                        {/* Horas vendidas por nível profissional (alimenta vendido×realizado). */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[11px] text-slate-400">Horas vendidas:</span>
                          {([["horasJunior", "Jr"], ["horasPleno", "Pl"], ["horasSenior", "Sr"]] as const).map(([campo, lbl]) => (
                            <div key={campo} className="flex items-center gap-1">
                              <span className="text-[11px] text-slate-400">{lbl}</span>
                              <input
                                type="number"
                                min={0}
                                step="0.5"
                                value={sel[campo]}
                                onChange={(e) => setItensSel((prev) => ({ ...prev, [item.id]: { ...prev[item.id], [campo]: e.target.value } }))}
                                placeholder="0"
                                className="w-14 px-1.5 py-1 rounded-md border border-slate-200 text-sm text-center outline-none focus:border-blue-400"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Marque os itens, ajuste quantidade, formato e as horas vendidas por nível (Jr/Pl/Sr) — alimentam o relatório contratado × entregue.
            </p>
          </div>
        )}

        {/* CADASTRO 3 — Tipo e Prioridade removidos do cadastro. Status fica como
            forma de avançar o projeto (em edição). */}
        {ehEdicao && (
          <div className="sm:max-w-xs">
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-blue-500 outline-none">
              <option value="planejamento">Planejamento</option>
              <option value="ativo">Ativo</option>
              {!form.interno && <option value="em_validacao">Em validação do cliente</option>}
              {!form.interno && <option value="entregue">Entregue (em suporte)</option>}
              <option value="concluido">Concluído</option>
              <option value="pausado">Pausado</option>
            </select>
          </div>
        )}

        {/* Atendimento na CRIAÇÃO. Em edição ele já aparece no bloco "Pessoas do
            projeto" lá em cima — repetir aqui daria dois controles pro mesmo
            campo, um deles fatalmente desatualizado na cabeça de quem usa. */}
        <div className={ehEdicao && id ? "hidden" : undefined}>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Atendimento <span className="text-red-500">*</span>
            <span className="ml-1 font-normal text-[11px] text-slate-500">— responsável pelo cliente</span>
          </label>
          {loadingGerentes ? (
            <div className="px-4 py-2.5 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-400">Carregando…</div>
          ) : gerentes.length === 0 ? (
            <div className="px-4 py-2.5 rounded-lg border border-red-200 bg-red-50 text-sm text-red-600">Nenhum gestor encontrado</div>
          ) : (
            <SearchableSelect
              items={gerentes}
              value={form.responsavelId}
              onChange={(rid) => setForm({ ...form, responsavelId: rid })}
              getId={(g) => g.id}
              getLabel={(g) => g.nome}
              getSecondary={(g) => g.email}
              placeholder="Selecione um gestor"
              allowClear={false}
            />
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Data Início *</label>
            <input type="date" required value={form.dataInicio} onChange={(e) => setForm({ ...form, dataInicio: e.target.value })}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-blue-500 outline-none" />
          </div>
          {/* CADASTRO 5 — prazo por modalidade. */}
          {!form.interno && form.modalidade === "mensal" ? (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Fim do contrato</label>
              {/* Os dois campos são EXCLUSIVOS, mas o de data não fica travado:
                  o fluxo real é "contrato era indeterminado → o cliente saiu →
                  ponho a data de saída". Se a data ficasse desabilitada enquanto
                  a caixinha estivesse marcada, a pessoa esbarraria num campo
                  cinza justo nessa hora. Então preencher a data DESMARCA sozinho,
                  e marcar a caixinha limpa a data. */}
              <input type="date" value={form.fimContrato}
                onChange={(e) => setForm({
                  ...form,
                  fimContrato: e.target.value,
                  contratoIndeterminado: e.target.value ? false : form.contratoIndeterminado,
                })}
                className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-blue-500 outline-none" />
              <label className="flex items-center gap-2 text-[12px] text-slate-600 mt-1.5 cursor-pointer">
                <input type="checkbox" checked={form.contratoIndeterminado}
                  onChange={(e) => setForm({
                    ...form,
                    contratoIndeterminado: e.target.checked,
                    fimContrato: e.target.checked ? "" : form.fimContrato,
                  })} />
                Contrato por tempo indeterminado
              </label>
              <p className="text-[11px] text-slate-400 mt-1">
                {form.contratoIndeterminado
                  ? "O contrato segue valendo todo mês. Quando o cliente sair, preencha a data acima — ele para de contar a partir dali."
                  : form.fimContrato
                    ? "A partir do mês seguinte a essa data o projeto deixa de contar nas previsões. O histórico anterior é preservado."
                    : "Sem data e sem marcar indeterminado, o projeto conta pra sempre nas previsões."}
              </p>
            </div>
          ) : !form.interno ? (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Prazo de entrega</label>
              <input type="text" value={form.prazoTexto}
                onChange={(e) => setForm({ ...form, prazoTexto: e.target.value })}
                placeholder="Ex.: 15 dias após briefing"
                className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-blue-500 outline-none" />
              <p className="text-[11px] text-slate-400 mt-1">Texto livre — varia por projeto (ex.: 20 dias após captação).</p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Prazo de entrega final</label>
              <input type="date" value={form.prazoEntrega} onChange={(e) => setForm({ ...form, prazoEntrega: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-blue-500 outline-none" />
            </div>
          )}
        </div>

        {/* MELHORIA 16 — data combinada com o cliente, ao lado do prazo, em roxo. Só gestão vê/seta. */}
        {!form.interno && can(["gestor", "admin"]) && (
          <div className="sm:max-w-xs">
            <label className="block text-sm font-medium text-purple-700 dark:text-purple-400 mb-1">🔒 Entrega ao cliente</label>
            <input
              type="date"
              value={form.dataEntregaCliente}
              onChange={(e) => setForm({ ...form, dataEntregaCliente: e.target.value })}
              className="w-full px-4 py-2.5 rounded-lg border border-purple-300 dark:border-purple-700 focus:border-purple-500 outline-none text-purple-700 dark:text-purple-300 dark:bg-slate-800"
            />
            <p className="text-[11px] text-slate-400 mt-1">Data combinada com o cliente, separada do prazo interno. Alimenta a pauta em Calendário → Entregas. Funcionário não vê.</p>
          </div>
        )}

        {/* Tempo de desenvolvimento — opcional, só avulso. */}
        {!form.interno && form.modalidade !== "mensal" && (
          <div className="sm:max-w-xs">
            <label className="block text-sm font-medium text-slate-700 mb-1">Tempo de desenvolvimento (dias)</label>
            <input
              type="number"
              min="1"
              step="1"
              value={form.tempoDesenvolvimentoDias}
              onChange={(e) => setForm({ ...form, tempoDesenvolvimentoDias: e.target.value })}
              placeholder="Ex: 60"
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-blue-500 outline-none"
            />
            <p className="text-[11px] text-slate-400 mt-1">Opcional — duração contratada em dias.</p>
          </div>
        )}

        {!form.interno && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
          <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">
            Valores do projeto <span className="text-amber-600 normal-case font-normal">(só admin/gestor vê)</span>
          </p>
          {/* CADASTRO 2 — um único campo de valor por modalidade. */}
          <div className="max-w-xs">
            {form.modalidade === "mensal" ? (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Mensalidade (mês)</label>
                <MoneyInput
                  value={form.valorRecorrente === "" ? 0 : Number(form.valorRecorrente)}
                  onChange={(n) => setForm({ ...form, valorRecorrente: n ? String(n) : "" })}
                />
                <p className="text-[11px] text-slate-500 mt-1">Valor recorrente cobrado por mês.</p>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Valor do projeto</label>
                <MoneyInput
                  value={form.valor === "" ? 0 : Number(form.valor)}
                  onChange={(n) => setForm({ ...form, valor: n ? String(n) : "" })}
                />
                <p className="text-[11px] text-slate-500 mt-1">Valor cheio do projeto avulso.</p>
              </div>
            )}
          </div>
          {/* CADASTRO 7 — custo extra para reembolso (não entra no valor do projeto). */}
          <div className="border-t border-amber-200 pt-3">
            <label className="block text-sm font-medium text-slate-700 mb-1">Custo extra (reembolso) <span className="text-[11px] font-normal text-slate-400">opcional</span></label>
            <div className="flex flex-col sm:flex-row gap-2 max-w-xl">
              <div className="w-40">
                <MoneyInput
                  value={form.custoExtra === "" ? 0 : Number(form.custoExtra)}
                  onChange={(n) => setForm({ ...form, custoExtra: n ? String(n) : "" })}
                />
              </div>
              <input
                type="text"
                value={form.custoExtraDescricao}
                onChange={(e) => setForm({ ...form, custoExtraDescricao: e.target.value })}
                placeholder="Do que se trata (ex.: cachê de ator, locação)"
                className="flex-1 px-3 py-2.5 rounded-lg border border-slate-300 focus:border-blue-500 outline-none text-sm"
              />
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Contratação à parte que será reembolsada pelo cliente. Não soma no valor do projeto.</p>
          </div>
          <p className="text-[11px] text-amber-700 bg-amber-100 border border-amber-200 rounded px-2 py-1.5">
            💡 Esses valores são a "ficha comercial" do projeto. Pra detalhar parcelas, vencimentos e gerar cobranças, crie o <strong>contrato financeiro</strong> no menu Financeiro.
          </p>
        </div>
        )}

        {/* CADASTRO 3 — "Fases do projeto" removido do cadastro. */}

        {!form.interno && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Resumo público <span className="text-[11px] font-normal text-slate-400">(o cliente VAI ver)</span></label>
          <textarea value={form.resumoPublico} onChange={(e) => setForm({ ...form, resumoPublico: e.target.value })}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-blue-500 outline-none" rows={2} placeholder="Texto curto que o cliente verá no portal." />
        </div>
        )}

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Descrição{!form.interno && <span className="text-[11px] font-normal text-slate-400 ml-1">(cliente NÃO vê)</span>}
          </label>
          <textarea value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-blue-500 outline-none" rows={3} placeholder="Detalhes técnicos, escopo interno, etc." />
        </div>


        {erro && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm space-y-1">
            <div className="font-medium">
              Erro ao {ehEdicao ? "salvar" : "criar"} projeto.
            </div>
            {erroMsg && <div className="text-xs text-red-600">{erroMsg}</div>}
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={submitting}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-6 py-2.5 rounded-lg font-medium transition-colors">
            <Save size={18} /> {submitting ? "Salvando..." : ehEdicao ? "Salvar alterações" : "Criar Projeto"}
          </button>
          <button type="button" onClick={() => navigate(ehEdicao ? `/projetos/${id}` : "/projetos")}
            className="px-6 py-2.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 font-medium transition-colors">
            Cancelar
          </button>
        </div>
      </form>

      {novoClienteAberto && (
        <ClienteFormModal
          editing={null}
          onClose={() => setNovoClienteAberto(false)}
          onSubmit={(dados) => criarClienteM.mutate(dados)}
          loading={criarClienteM.isPending}
          error={(criarClienteM.error as any)?.response?.data?.error?.message}
        />
      )}
    </div>
  );
}

function FotoProjetoEditor({ projetoId, nome, fotoUrl, cor, onChange }: {
  projetoId: string;
  nome: string;
  fotoUrl: string | null;
  cor: string;
  onChange: () => void;
}) {
  const [enviando, setEnviando] = useState(false);

  async function aoSelecionar(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setEnviando(true);
    try {
      await uploadFotoProjeto(projetoId, f);
      onChange();
    } catch (err) {
      console.error(err);
      alert("Falha no upload da imagem.");
    } finally {
      setEnviando(false);
      e.target.value = "";
    }
  }

  async function aoRemover() {
    setEnviando(true);
    try {
      await removerFotoProjeto(projetoId);
      onChange();
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">Imagem do projeto</label>
      <div className="flex items-center gap-3">
        <ProjetoAvatar nome={nome} fotoUrl={fotoUrl} cor={cor} tamanho="lg" />
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 cursor-pointer bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 text-sm px-3 py-1.5 rounded-lg">
            <Upload size={14} /> {fotoUrl ? "Trocar imagem" : "Subir imagem"}
            <input type="file" accept="image/*" className="hidden" onChange={aoSelecionar} disabled={enviando} />
          </label>
          {fotoUrl && (
            <button
              type="button"
              onClick={aoRemover}
              disabled={enviando}
              className="inline-flex items-center gap-1 text-red-600 hover:bg-red-50 border border-red-200 text-sm px-2.5 py-1.5 rounded-lg disabled:opacity-50"
            >
              <Trash2 size={14} /> Remover
            </button>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-1">
        Opcional. Quando vazio, o card mostra as iniciais ({nome ? <strong>{nome.trim().split(/\s+/).slice(0,2).map(p=>p[0]).join("").toUpperCase()}</strong> : "—"}) sobre a cor do tipo. Máx. 5 MB.
      </p>
    </div>
  );
}

/**
 * Pessoas do projeto num bloco só (jul/2026, pedido do Ricardo — referência
 * visual: popover de membros do Trello). Antes eram duas seções distantes uma
 * da outra: o select "Gestor Responsável" lá em cima e a caixa "Equipe do
 * projeto" com busca sempre aberta lá embaixo. Junta os dois e troca a caixa
 * permanente por avatares + popover, economizando ~150px de altura.
 *
 * DOIS PAPÉIS, e os nomes importam:
 *  - ATENDIMENTO = `projeto.responsavelId` (1 pessoa, gestor). Quem responde ao
 *    cliente. Campo que já existia como "Gestor Responsável".
 *  - EXECUTORES  = `ProjetoMembro` (N pessoas). Quem toca o trabalho. Campo que
 *    já existia como "Equipe do projeto".
 * Nenhum dos dois é novo no banco — a mudança aqui é de NOME e de layout.
 *
 * ATENÇÃO à semântica de salvamento, que é diferente entre os dois e foi
 * mantida como já era pra não surpreender ninguém:
 *  - atendimento entra no form e só grava no "Salvar" do projeto;
 *  - executores gravam NA HORA (mutation própria).
 * Por isso o aviso "salvo na hora" fica escrito ao lado dos executores.
 */
function PessoasProjeto({
  projetoId,
  responsavelId,
  gerentes,
  onChangeResponsavel,
}: {
  projetoId: string;
  responsavelId: string;
  gerentes: GerenteOption[];
  onChangeResponsavel: (id: string) => void;
}) {
  const qc = useQueryClient();
  const { data: membros = [] } = useQuery({
    queryKey: ["projeto-membros", projetoId],
    queryFn: () => listarMembrosProjeto(projetoId),
  });
  const { data: usuarios = [] } = useQuery({
    queryKey: ["usuarios-ativos"],
    queryFn: listarUsuariosAtivos,
  });

  // Qual popover está aberto (só um por vez).
  const [aberto, setAberto] = useState<null | "atendimento" | "executores">(null);
  const [busca, setBusca] = useState("");
  const caixaRef = useRef<HTMLDivElement>(null);

  // Fecha ao clicar fora ou no ESC. Popover PODE fechar no clique fora — a regra
  // da Rec de "só fecha no X" vale pra modal de formulário, onde se perde texto
  // digitado; aqui o único conteúdo é o termo de busca, que é descartável.
  useEffect(() => {
    if (!aberto) return;
    const clicouFora = (e: MouseEvent) => {
      if (caixaRef.current && !caixaRef.current.contains(e.target as Node)) {
        setAberto(null);
        setBusca("");
      }
    };
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation(); // não deixa o ESC vazar e fechar a página/modal de trás
        setAberto(null);
        setBusca("");
      }
    };
    document.addEventListener("mousedown", clicouFora);
    document.addEventListener("keydown", aoTeclar, true);
    return () => {
      document.removeEventListener("mousedown", clicouFora);
      document.removeEventListener("keydown", aoTeclar, true);
    };
  }, [aberto]);

  const atendimento = gerentes.find((g) => g.id === responsavelId) || null;
  const idsAtuais = new Set(membros.map((m) => m.id));

  const filtrar = (lista: any[]) => {
    const t = busca.trim().toLowerCase();
    if (!t) return lista;
    return lista.filter(
      (u: any) => u.nome.toLowerCase().includes(t) || (u.email || "").toLowerCase().includes(t),
    );
  };

  // Executor não pode ser o atendimento (ele já enxerga tudo do projeto) nem
  // cliente, nem quem já está na lista.
  const candidatos = filtrar(
    (usuarios || []).filter(
      (u: any) => u.id !== responsavelId && u.perfil !== "cliente" && !idsAtuais.has(u.id),
    ),
  );

  const salvarM = useMutation({
    mutationFn: (ids: string[]) => definirMembrosProjeto(projetoId, ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projeto-membros", projetoId] });
      qc.invalidateQueries({ queryKey: ["projetos"] });
    },
  });

  const adicionar = (uid: string) => salvarM.mutate([...membros.map((m) => m.id), uid]);
  const remover = (uid: string) =>
    salvarM.mutate(membros.filter((m) => m.id !== uid).map((m) => m.id));

  return (
    <div ref={caixaRef} className="relative">
      <label className="block text-sm font-medium text-slate-700 mb-1 inline-flex items-center gap-1.5">
        <UsersIcon size={14} /> Pessoas do projeto
      </label>
      <p className="text-[11px] text-slate-500 mb-2">
        Funcionário e suporte só veem projetos onde são atendimento ou executor. Gestor e admin veem tudo.
      </p>

      <div className="flex flex-wrap items-start gap-4 p-3 rounded-lg border border-slate-300 bg-white">
        {/* ATENDIMENTO */}
        <div className="min-w-[170px]">
          <p className="text-[11px] font-medium text-slate-500 mb-1.5">Atendimento</p>
          <button
            type="button"
            onClick={() => { setAberto(aberto === "atendimento" ? null : "atendimento"); setBusca(""); }}
            className="inline-flex items-center gap-2 pl-1 pr-2 py-1 rounded-full border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-colors max-w-full"
            title="Quem responde ao cliente neste projeto"
          >
            {atendimento ? (
              <>
                <Avatar nome={atendimento.nome} tamanho={22} bgFallback="bg-emerald-100" textFallback="text-emerald-700" />
                <span className="text-xs text-slate-700 truncate">{atendimento.nome}</span>
              </>
            ) : (
              <>
                <span className="w-[22px] h-[22px] rounded-full bg-slate-100 text-slate-400 flex items-center justify-center">
                  <UserPlus size={12} />
                </span>
                <span className="text-xs text-slate-400">definir…</span>
              </>
            )}
          </button>
        </div>

        {/* EXECUTORES */}
        <div className="min-w-[170px]">
          <p className="text-[11px] font-medium text-slate-500 mb-1.5">
            Executores
            {salvarM.isPending ? (
              <span className="ml-1 text-blue-500">salvando…</span>
            ) : (
              <span className="ml-1 text-slate-400 font-normal">· salvo na hora</span>
            )}
          </p>
          <div className="flex items-center gap-1 flex-wrap">
            {membros.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => remover(m.id)}
                disabled={salvarM.isPending}
                className="group relative rounded-full ring-2 ring-white hover:ring-red-200 transition-all disabled:opacity-50"
                title={`${m.nome} — clique para remover`}
              >
                <Avatar nome={m.nome} fotoUrl={m.fotoUrl} tamanho={26} bgFallback="bg-blue-100" textFallback="text-blue-700" />
                <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-red-500 text-white items-center justify-center hidden group-hover:flex">
                  <XIcon size={9} />
                </span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => { setAberto(aberto === "executores" ? null : "executores"); setBusca(""); }}
              className="w-[26px] h-[26px] rounded-full border border-dashed border-slate-300 text-slate-400 flex items-center justify-center hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
              title="Adicionar executor"
            >
              <UserPlus size={13} />
            </button>
            {membros.length === 0 && (
              <span className="text-[11px] text-slate-400 italic ml-1">ninguém ainda</span>
            )}
          </div>
        </div>
      </div>

      {/* POPOVER — mesma caixa pros dois papéis, muda só a lista. */}
      {aberto && (
        <div className="absolute z-30 mt-1 w-72 rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-700">
              {aberto === "atendimento" ? "Definir atendimento" : "Adicionar executor"}
            </span>
            <button
              type="button"
              onClick={() => { setAberto(null); setBusca(""); }}
              className="text-slate-400 hover:text-slate-600"
              title="Fechar"
            >
              <XIcon size={14} />
            </button>
          </div>

          <div className="p-2">
            <input
              type="text"
              autoFocus
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Pesquisar pessoas"
              className="w-full px-3 py-1.5 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500"
            />
          </div>

          <div className="max-h-56 overflow-y-auto pb-1">
            {aberto === "atendimento" ? (
              filtrar(gerentes).length === 0 ? (
                <p className="px-3 py-2 text-[11px] text-slate-400">Nenhum gestor encontrado.</p>
              ) : (
                filtrar(gerentes).map((g: any) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => { onChangeResponsavel(g.id); setAberto(null); setBusca(""); }}
                    className={
                      "w-full px-3 py-1.5 text-left text-sm flex items-center gap-2 hover:bg-slate-50 " +
                      (g.id === responsavelId ? "bg-emerald-50" : "")
                    }
                  >
                    <Avatar nome={g.nome} tamanho={24} bgFallback="bg-emerald-100" textFallback="text-emerald-700" />
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-slate-700">{g.nome}</p>
                      <p className="text-[10px] text-slate-400 truncate">{g.email}</p>
                    </div>
                    {g.id === responsavelId && <span className="text-[10px] text-emerald-600 font-medium">atual</span>}
                  </button>
                ))
              )
            ) : candidatos.length === 0 ? (
              <p className="px-3 py-2 text-[11px] text-slate-400">
                {busca.trim() ? "Sem resultados." : "Todo mundo já está no projeto."}
              </p>
            ) : (
              candidatos.slice(0, 20).map((u: any) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => { adicionar(u.id); setBusca(""); }}
                  disabled={salvarM.isPending}
                  className="w-full px-3 py-1.5 text-left text-sm flex items-center gap-2 hover:bg-slate-50 disabled:opacity-50"
                >
                  <Avatar nome={u.nome} fotoUrl={u.fotoUrl} tamanho={24} bgFallback="bg-slate-100" textFallback="text-slate-600" />
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-slate-700">{u.nome}</p>
                    <p className="text-[10px] text-slate-400 truncate">{u.email} · {u.perfil}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
